import React, { useState, useRef, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import Slider from '../../hocs/Slider/Slider';
import BackgroundMedia from './BackgroundMedia/BackgroundMedia';
import Button from './Button/Button';
import Embed from './Embed/Embed';
import Heading from './Heading/Heading';
import Image from './Image/Image';
import List from './List/List';
import Paragraph from './Paragraph/Paragraph';
import Placeholder from './Placeholder/Placeholder';
import Products from './Products/Products';
import Quote from './Quote/Quote';
import Separator from './Separator/Separator';
import Spacer from './Spacer/Spacer';
import Table from './Table/Table';
import Ticker from './Ticker/Ticker';
import Video from './Video/Video';
import VideoEmbed from './VideoEmbed/VideoEmbed';
import Wash from './Wash/Wash';
import { percentToColumn } from '../../utils/columns';
import { addEvent, removeEvent, addClass, removeClass, offset, height, purifier } from '../../utils/dom';
import { translate, translateObjectDefaultData, prefix } from '../../utils/locale';
import { deepMerge } from '../../utils/object';
import { titleCase, urlify, camelCase, decode, cleanInternalLinks } from '../../utils/string';
import { isRestricted } from '../../utils/restrict';
import s from './TemplateParser.scss';

const components = {
	acfPlaceholder: Placeholder,
	acfProducts: Products,
	acfTicker: Ticker,
	acfWash: Wash,
	coreButton: Button,
	coreEmbed: VideoEmbed,
	coreHeading: Heading,
	coreHtml: Embed,
	coreImage: Image,
	coreParagraph: Paragraph,
	coreQuote: Quote,
	coreSeparator: Separator,
	coreSpacer: Spacer,
	coreTable: Table,
	coreVideo: Video,
};

const containers = {};

const heightSettings = ({ attrs = {}, setup }) => {
	const extraProps = {};
	const settings = {};

	if ((attrs.fixedHeightValue && setup.breakPoint === 'desktop')
		|| (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet')
		|| (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile')
	) {
		extraProps.className = ['u_fillContainer'];
		extraProps.style = {
			overflow: 'hidden',
		};
		settings.fill = true;
		settings.focalPoint = attrs.focalPoint;
	}

	if (attrs.fixedHeightValue && setup.breakPoint === 'desktop') extraProps.style.height = `${attrs.fixedHeightValue}vh`;
	if (attrs.fixedHeightValueMd && setup.breakPoint === 'tablet') extraProps.style.height = `${attrs.fixedHeightValueMd}vh`;
	if (attrs.fixedHeightValueSm && setup.breakPoint === 'mobile') extraProps.style.height = `${attrs.fixedHeightValueSm}vh`;

	return [extraProps, settings];
};

const generalSettings = ({ customProps, attrs = {}, template }) => {
	const attrsClassName = attrs.className || [];

	const generalProps = {
		className: [
			s.container,
			s[template],
			...attrsClassName.map((cN) => s[`${template}${cN}`]),
		],
		style: {},
	};

	// Colors
	if (attrs.backgroundColor) generalProps.style.backgroundColor = attrs.backgroundColor;
	if (attrs.textColor) {
		generalProps.style.color = attrs.textColor;
		generalProps.className.push('u_white');
	}

	// Alignment
	if (attrs.align && attrs.align !== 'full') generalProps.style.textAlign = attrs.align;
	if (attrs.contentJustification && attrs.contentJustification !== 'full') generalProps.style.justifyContent = attrs.contentJustification;
	if (attrs.verticalAlignment) generalProps.className[generalProps.className.length] = s[`container__v${titleCase(attrs.verticalAlignment)}`];
	if (attrs.align === 'full') generalProps.className[generalProps.className.length] = 'u_fullWidth';

	const extraProps = deepMerge(generalProps, customProps);
	extraProps.className = extraProps.className.join(' ');

	return extraProps;
};

const getElement = (setup, attrs) => {
	const extraProps = {};
	let Element = 'div';

	if (attrs.href) {
		const link = cleanInternalLinks(setup, attrs.href);
		const externalLink = /^(https?|mailto|tel)/.test(link);
		const anchorLink = /^#/.test(link);

		if (attrs.target || externalLink || anchorLink) {
			Element = 'a';
			extraProps.href = link;
			extraProps.target = '_blank';
			extraProps.rel = 'noopener noreferrer';
		} else {
			Element = Link;
			extraProps.to = link.replace(/\/+$/, '');
		}
	}

	return [Element, extraProps];
};

const getTemplate = (template, attrs, setup) => {
	if (isRestricted(setup, attrs)) return null;
	if (components[template]) return components[template];
	if (containers[template]) return containers[template];

	console.log('DEBUG: missing template', template, containers, components);
	return null;
};

const scrollRight = (e) => {
	e.preventDefault();
	e.currentTarget.nextSibling.scrollLeft += (window.innerWidth * 0.7) + 5;
};

containers.acfAbout = ({ aboutListing, content, setup }) => {
	if (!aboutListing || !aboutListing?.content) return null;
	const aboutContainer = useRef(null);
	const aboutListingOrdered = aboutListing.content.filter((a) => !!a?.meta?.default?.uri).sort((a, b) => a.meta.default.uri.localeCompare(b.meta.default.uri));
	const aboutListingOrderedLetters = aboutListingOrdered.map((a) => a.meta.default.uri.slice(0, 1).toLowerCase());
	const singlePostTitle = content.letter?.posts?.[0]?.meta?.default?.uri;
	const matchingLetter = singlePostTitle ? singlePostTitle.slice(0, 1).toLowerCase() : false;
	const initialPost = aboutListingOrderedLetters.indexOf(matchingLetter) !== -1 ? aboutListingOrderedLetters.indexOf(matchingLetter) : false;
	const [currentPost, updatePost] = useState(initialPost);

	const changePostHandler = (e) => {
		e.preventDefault();
		const l = e.currentTarget.textContent;
		let index = aboutListingOrderedLetters.indexOf(l.toLowerCase());
		index = index === -1 ? false : index;
		updatePost((prevState) => index === prevState ? false : index);
	};

	useEffect(() => {
		const $sections = aboutContainer.current.querySelectorAll('section');
		let positions;
		let lastChecked = window.performance.now();
		let inView = [];
		const getPositions = () => (
			[...$sections].map((section) => (
				[offset(section).top, height(section)]
			))
		);

		const viewingAboutSection = () => {
			if (window.performance.now() > lastChecked + 5000) {
				positions = getPositions();
				lastChecked = window.performance.now();
			}
			const top = window.pageYOffset || document.documentElement.scrollTop;
			for (let i = 0; i < positions.length; i += 1) {
				const [sOffset, sHeight] = positions[i];
				if (inView.indexOf(i) === -1 && top > (sOffset - (window.innerHeight / 2)) && top < (sOffset + sHeight - (window.innerHeight / 4))) {
					inView = [...inView, i];
					addClass([...$sections][i], 'u_isVisible');
				} else if (inView.indexOf(i) !== -1 && (top < (sOffset - (window.innerHeight / 2)) || top > (sOffset + sHeight - (window.innerHeight / 4)))) {
					inView = [...inView.slice(0, inView.indexOf(i)), ...inView.slice(inView.indexOf(i) + 1)];
					removeClass([...$sections][i], 'u_isVisible');
				}
			}
		};

		addEvent(window, 'scroll.aboutSections', () => {
			window.requestAnimationFrame(viewingAboutSection);
		});

		positions = getPositions();
		viewingAboutSection();

		return () => {
			removeEvent(window, 'scroll.aboutSections');
		};
	}, [currentPost]);

	return (
		<div className={s.acfAbout} ref={aboutContainer}>
			<div className={s.acfAbout_alphabet}>
				<p className={s.acfAbout_alphabet_title}>{ translate(setup, 'aboutHeading') }</p>
				<div className={s.acfAbout_alphabet_letters}>
					{
						aboutListingOrdered.length > 0 && aboutListingOrdered.map((post, i) => {
							const postLetter = post.meta.default.uri.slice(0, 1).toUpperCase();
							const translatedPostMeta = translateObjectDefaultData(setup, post.meta);

							return <Link key={post.meta.default.uri} className={[s.acfAbout_alphabet_letter, currentPost === i ? s.acfAbout_alphabet_letter__active : null].join(' ')} to={prefix(setup, `/${translatedPostMeta.fullUri}`)} onClick={changePostHandler}>{ postLetter }</Link>;
						})
					}
				</div>
			</div>
			<div className={s.acfAbout_content}>
				{
					currentPost !== false && aboutListingOrdered[currentPost] && (
						<section className={s.acfAbout_content_section}>
							<div className={s.acfAbout_content_section_inner}>
								<TemplateParser sections={aboutListingOrdered[currentPost].sections} setup={setup} about />
							</div>
						</section>
					)
				}
				{
					currentPost === false && content.showAll && aboutListingOrdered.length > 0 && aboutListingOrdered.map((post, i) => (
						<section key={post.meta.default.uri} className={s.acfAbout_content_section}>
							{ i > 0 ? <h2 className={s.acfAbout_content_section_intro}>{ post.meta.default.title.slice(0, 1) }</h2> : null }
							{
								post.sections.length > 0 && (
									<div className={s.acfAbout_content_section_inner}>
										<TemplateParser sections={post.sections} setup={setup} about />
									</div>
								)
							}
						</section>
					))
				}
			</div>
		</div>
	);
};

containers.ccBlocksAccordion = ({ attrs, content, setup, template, sectionItem = false, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} sectionItem={`${sectionItem ? `${sectionItem}.` : ''}${i + 1}`} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.ccBlocksAccordionItem = ({ attrs, content, setup, sectionItem, template, ...props }) => {
	const customProps = {};

	const [accordianToggled, toggleAccordian] = useState(attrs.open || false);
	const toggleAccordianHandler = (e) => {
		e.preventDefault();
		toggleAccordian((prevState) => !prevState);
	};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			<button aria-controls={urlify(attrs.title.slice(0, 50))} aria-expanded={accordianToggled} type="button" onClick={toggleAccordianHandler} className={[s.ccBlocksAccordionItem_title, (accordianToggled ? s.ccBlocksAccordionItem_title__expanded : null)].join(' ')}>
				<span>{`${sectionItem}. `}</span>
				<span>{ decode(attrs.title) }</span>
			</button>
			<div aria-hidden={!accordianToggled} id={urlify(attrs.title.slice(0, 50))} className={[s.ccBlocksAccordionItem_content, (accordianToggled ? s.ccBlocksAccordionItem_content__expanded : null)].join(' ')}>
				<div className={[content.filter((childSection) => Object.keys(childSection)[0] === 'ccBlocksAccordion').length === 0 ? s.ccBlocksAccordionItem_content_inner : s.ccBlocksAccordionItem_content_outer, (props.isOverlay ? s.ccBlocksAccordionItem_content_inner__overlay : null)].join(' ')}>
					{
						content.map((childSection) => {
							const tpl = Object.keys(childSection)[0];
							const nextContent = childSection[tpl];
							const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
							if (!ChildElement) return null;
							return <ChildElement template={tpl} key={nextContent.key} sectionItem={sectionItem} setup={setup} {...nextContent} {...props} />;
						})
					}
				</div>
			</div>
		</div>
	);
};

containers.ccBlocksPuff = ({ attrs, content, uid, setup, template, ...props }) => {
	const puffContainer = useRef(null);
	const [customProps, settings] = heightSettings({ attrs, setup });

	useEffect(() => {
		if (!attrs?.overlayColor && !attrs.animate) return () => {};

		const puffInView = (delay) => {
			const puffOffset = offset(puffContainer.current);
			const top = window.pageYOffset || document.documentElement.scrollTop;
			if (top + window.innerHeight * 0.5 > puffOffset.top) {
				if (delay) {
					window.setTimeout(() => {
						addClass(puffContainer.current, 'u_isVisible');
					}, 800);
				} else {
					addClass(puffContainer.current, 'u_isVisible');
				}
				removeEvent(window, `scroll.puffOverlay_${uid}`);
			}
		};

		addEvent(window, `scroll.puffOverlay_${uid}`, () => {
			window.requestAnimationFrame(() => {
				puffInView();
			});
		});

		puffInView(true);

		return () => {
			removeEvent(window, `scroll.puffOverlay_${uid}`);
		};
	}, [puffContainer, attrs]);

	const [Element, elementProps] = getElement(setup, attrs);
	customProps.className = [...(customProps.className || []), s[`container__${camelCase(attrs.layout || 'center Center')}`]];
	if (attrs.animate) customProps.className.push('u_animate');
	const extraProps = generalSettings({ customProps: deepMerge(customProps, elementProps), attrs, template });

	return (
		<Element {...extraProps}>
			<div id={uid} ref={puffContainer}>
				<BackgroundMedia content={attrs} uid={uid} {...settings} {...props} />
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						if (attrs.animate && tpl === 'coreGroup') nextContent.animate = true;
						if (tpl === 'coreColumns') nextContent.puffSubHeader = true;
						return <ChildElement template={tpl} key={nextContent.key} isPuffContent setup={setup} {...nextContent} {...props} />;
					})
				}
			</div>
		</Element>
	);
};
containers.ccBlocksPuffAlt = containers.ccBlocksPuff;

containers.ccBlocksSlider = ({ attrs, content, setup, template, ...props }) => {
	const [customProps] = heightSettings({ attrs, setup });
	let Wrapper;

	const sliderAttrs = {
		transitionType: attrs?.transitionType || 'fade',
		transitionSpeed: attrs?.transitionSpeed || 300,
		infinite: 1,
		hint: attrs?.hint || false,
		autoplay: attrs?.autoplay === false ? false : attrs.transitionDelay || 3000,
		interactionTypes: ['touch'],
		adaption: 'content',
		showCounter: false,
	};

	const extraProps = generalSettings({ customProps, attrs, template });

	if (attrs?.freeFlow && attrs?.align === 'full') {
		Wrapper = ({ children }) => (
			<div className={s.container__ccBlocksSlider_inner}>
				<button className={s.container__ccBlocksSlider_scroll} onClick={scrollRight} type="button" aria-label="Scroll Right">
					<svg width="7" height="10" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 5L1 9" stroke="black" strokeWidth="0.3" /></svg>
					<svg width="12" height="18" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L11 9L1 17" stroke="black" strokeWidth="0.3" /></svg>
				</button>
				<div className={s.container__ccBlocksSlider_inner_overflow}>
					{ children }
				</div>
			</div>
		);
	} else {
		Wrapper = ({ children }) => (
			<Slider {...sliderAttrs}>
				{ children }
			</Slider>
		);
	}

	return (
		<div {...extraProps}>
			<Wrapper>
				{
					content.map((childSection) => {
						const tpl = Object.keys(childSection)[0];
						const nextContent = childSection[tpl];
						const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
						if (!ChildElement) return null;
						return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
					})
				}
			</Wrapper>
		</div>
	);
};

containers.coreButtons = ({ attrs, content, setup, template, ...props }) => {
	const customProps = {
		className: ['u_buttons'],
	};
	if (!content) return null;
	const isManyButtons = content.filter((tpl) => !isRestricted(setup, tpl[Object.keys(tpl)[0]].attrs));

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} isManyButtons={isManyButtons.length} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreColumn = ({ attrs, content = [], setup, template, column, ...props }) => {
	const customProps = {
		className: [],
	};

	if (attrs?.width && attrs.width.indexOf('px') !== -1) {
		customProps.className = ['u_column', 'u_width__set'];
		customProps.style = { width: attrs.width };
	} else if (column) customProps.className = ['u_column', `u_width__${column}`];
	if (!attrs?.width) customProps.className.push('u_width__grow');

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content && content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} column={column} setup={setup} {...nextContent} {...props} uid={nextContent.key} />;
				})
			}
		</div>
	);
};

containers.coreColumns = ({ attrs, content = [], setup, template, puffSubHeader = false, ...props }) => {
	const customProps = {};

	if (typeof attrs?.isStackedOnMobile !== 'undefined' && attrs.isStackedOnMobile === 0) customProps.className = [s.coreColumns__columnsRowMobile];
	if (props?.inlineCategoryContent) customProps.className = [s.coreColumns__inlineCategoryContent];

	const initialColumnWidths = percentToColumn(content);
	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection, i) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					if (puffSubHeader && i > 0) props.puffSubHeader = true;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} column={props?.column !== undefined ? false : initialColumnWidths[i]} />;
				})
			}
		</div>
	);
};

containers.coreListItem = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<li {...extraProps}>
			{ attrs?.content?.rawText && <div dangerouslySetInnerHTML={{ __html: purifier(attrs.content.rawText) }} /> }
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</li>
	);
};

containers.coreList = ({ attrs = {}, content = [], setup, template, ...props }) => {
	const customProps = {};

	if (!Array.isArray(content)) return <List content={content} {...props} />;

	if (props.inlineCategoryContent) customProps.className = [s.coreList__inlineCategoryContent];
	const extraProps = generalSettings({ customProps, attrs, template });

	let Wrapper = ({ children }) => (
		<ul {...extraProps}>
			{ children }
		</ul>
	);

	if (attrs?.ordered) {
		Wrapper = ({ children }) => (
			<ol {...extraProps}>
				{ children }
			</ol>
		);
	}

	return (
		<Wrapper>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</Wrapper>
	);
};

containers.coreGroup = ({ attrs, content = [], setup, template, ...props }) => {
	const customProps = {};

	const extraProps = generalSettings({ customProps, attrs, template });

	return (
		<div {...extraProps}>
			{
				content.map((childSection) => {
					const tpl = Object.keys(childSection)[0];
					const nextContent = childSection[tpl];
					const ChildElement = getTemplate(tpl, nextContent.attrs, setup);
					if (!ChildElement) return null;
					return <ChildElement template={tpl} key={nextContent.key} setup={setup} {...nextContent} {...props} />;
				})
			}
		</div>
	);
};

containers.coreMore = () => null;

const NoContainer = ({ attrs, content, template, setup, ...props }) => {
	const Template = getTemplate(template, attrs, setup);
	if (!Template) return null;

	let Wrapper = ({ children }) => children;

	// Add wrappers
	if (!props.inline && attrs?.align !== 'full' && template !== 'acfProducts' && template !== 'acfPlaceholder') {
		Wrapper = ({ children }) => (
			<div className={s.container__textPage}>
				<div className={s.container_inner}>{ children }</div>
			</div>
		);
	}

	return (
		<ErrorBoundary message={`CMS TemplateSection: ${template}`} debugData={content}>
			<Wrapper>
				<Template content={content} attrs={attrs} setup={setup} {...props} />
			</Wrapper>
		</ErrorBoundary>
	);
};

const TemplateParser = ({ sections, setup, variantClasses = false, ...props }) => {
	if (!sections) return null;
	if (!Array.isArray(sections)) sections = [sections];
	const templateContents = [];
	let first = true;
	let classes = [];
	if (variantClasses) {
		classes = Array.isArray(variantClasses) ? variantClasses : [variantClasses];
		classes = [...classes.filter((c) => !!c).map((c) => s[c])];
	}

	sections.forEach((section, i) => {
		const template = Object.keys(section)[0];
		if (section[template]) {
			if (isRestricted(setup, section[template].attrs)) return;
			const ChildElement = containers[template] || NoContainer;
			if (ChildElement) {
				const Wrapper = ({ children }) => (classes.length > 0 ? <div className={classes.join(' ')}>{ children }</div> : children);
				const nextContent = section[template];
				templateContents[templateContents.length] = (
					<Wrapper key={nextContent.key}>
						<ChildElement template={template} setup={setup} first={first} last={sections.length - 1 === i} uid={nextContent.key} {...nextContent} {...props} />
					</Wrapper>
				);
			}
		}
		first = false;
	});

	return templateContents;
};

const mapState = ({ listing }, props) => ({
	aboutListing: listing?.about,
	...props,
});

export default hot(module)(connect(mapState)(TemplateParser));
